/**
 * Mission 35: Admin Campaign List Page
 *
 * Follows AdminSupportTickets.tsx pattern exactly:
 * - Stats cards (Active, Draft, Paused, Completed)
 * - Filter pills (All / Announcement / Notice / Chat / Survey)
 * - Status dropdown, search input
 * - Table with row click navigation
 * - Inline styles, lucide-react icons, same color palette
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminCampaignService } from '../services/adminCampaignService';

const TYPE_LABELS: Record<string, string> = {
  announcement: 'Announcement',
  contextual_notice: 'Notice',
  cedar_chat: 'Chat Message',
  survey: 'Survey',
};

const TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  announcement: { bg: '#DBEAFE', text: '#1E40AF' },
  contextual_notice: { bg: '#FEF3C7', text: '#92400E' },
  cedar_chat: { bg: '#EDE9FE', text: '#5B21B6' },
  survey: { bg: '#D1FAE5', text: '#065F46' },
};

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  draft: { bg: '#F3F4F6', text: '#374151' },
  active: { bg: '#D1FAE5', text: '#065F46' },
  paused: { bg: '#FEF3C7', text: '#92400E' },
  completed: { bg: '#DBEAFE', text: '#1E40AF' },
  archived: { bg: '#F3F4F6', text: '#6B7280' },
};

const TYPE_FILTERS = ['all', 'announcement', 'contextual_notice', 'cedar_chat', 'survey'];

interface Stats {
  active: number;
  draft: number;
  paused: number;
  completed: number;
  total: number;
}

interface Campaign {
  id: string;
  name: string;
  campaign_type: string;
  status: string;
  impressions: number;
  seen?: number;
  cta_taps: number;
  ctr: number;
  step_count: number;
  created_at: string;
  updated_at: string;
}

interface CampaignAnalytics {
  delivered?: number;
  impressions: number;
  seen?: number;
  cta_taps: number;
  closed?: number;
  ignored?: number;
  ctr: number;
  unique_users?: number;
  per_step?: Array<{
    step_id: string;
    message_type?: string;
    delivered?: number;
    seen?: number;
    impressions: number;
    cta_taps: number;
    closed?: number;
    ctr: number;
  }>;
}

interface SurveyQuestion {
  question_id: string;
  question_text: string;
  response_type: 'yes_no' | 'rating' | 'multiple_choice' | 'free_text';
  options?: string[];
  responses: Record<string, number>;
  free_text_responses?: string[];
  total_responses: number;
}

interface SurveyResults {
  questions: SurveyQuestion[];
  total_responses: number;
  completion_rate: number;
  funnel: { question_id: string; question_text: string; answered: number }[];
}

export const AdminCampaigns: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Detail panel
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [analytics, setAnalytics] = useState<CampaignAnalytics | null>(null);
  const [surveyResults, setSurveyResults] = useState<SurveyResults | null>(null);
  const [panelLoading, setPanelLoading] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<string | null>(null);

  const openDetail = useCallback(async (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setAnalytics(null);
    setSurveyResults(null);
    setPanelLoading(true);
    try {
      const analyticsData = await AdminCampaignService.getAnalytics(campaign.id);
      setAnalytics(analyticsData);
      if (campaign.campaign_type === 'survey') {
        const surveyData = await AdminCampaignService.getSurveyResults(campaign.id);
        setSurveyResults(surveyData);
      }
    } catch (e) {
      console.error('Failed to load campaign details:', e);
    }
    setPanelLoading(false);
  }, []);

  const handleExport = useCallback(async (format: 'csv' | 'xlsx') => {
    if (!selectedCampaign) return;
    setExportingFormat(format);
    try {
      const blob = await AdminCampaignService.exportSurveyResults(selectedCampaign.id, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedCampaign.name.replace(/\s+/g, '_')}_survey_results.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to export survey results:', e);
    }
    setExportingFormat(null);
  }, [selectedCampaign]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await AdminCampaignService.getStats();
      setStats(data);
    } catch (e) {
      console.error('Failed to fetch campaign stats:', e);
    }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const data = await AdminCampaignService.listCampaigns({
        campaign_type: typeFilter !== 'all' ? typeFilter : undefined,
        status: statusFilter || undefined,
        search: searchQuery || undefined,
      });
      setCampaigns(data.campaigns || []);
    } catch (e) {
      console.error('Failed to fetch campaigns:', e);
    }
    setLoading(false);
  }, [typeFilter, statusFilter, searchQuery]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const statCards = stats ? [
    { label: 'Active', value: stats.active, color: '#065F46', bg: '#D1FAE5' },
    { label: 'Draft', value: stats.draft, color: '#374151', bg: '#F3F4F6' },
    { label: 'Paused', value: stats.paused, color: '#92400E', bg: '#FEF3C7' },
    { label: 'Completed', value: stats.completed, color: '#1E40AF', bg: '#DBEAFE' },
  ] : [];

  return (
    <div style={{ padding: 32 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0B1614', margin: 0, fontFamily: "'League Spartan', sans-serif" }}>Campaigns</h1>
        <button
          onClick={() => navigate('/admin/campaigns/new')}
          style={{
            padding: '10px 24px', borderRadius: 10, border: 'none',
            background: '#1F6C4F', color: '#fff', fontSize: 14,
            fontWeight: 600, cursor: 'pointer',
          }}
        >
          + New Campaign
        </button>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {statCards.map((card) => (
          <div
            key={card.label}
            style={{
              padding: 20, borderRadius: 14, background: card.bg,
              boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)',
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: card.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {card.label}
            </div>
            <div style={{ fontSize: 32, fontWeight: 700, color: card.color, marginTop: 4 }}>
              {card.value}
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        {TYPE_FILTERS.map((t) => (
          <button
            key={t}
            onClick={() => setTypeFilter(t)}
            style={{
              padding: '6px 16px', borderRadius: 100,
              border: '1.5px solid', fontSize: 13, fontWeight: 500, cursor: 'pointer',
              borderColor: typeFilter === t ? '#1F6C4F' : '#d1d5db',
              background: typeFilter === t ? '#1F6C4F' : '#fff',
              color: typeFilter === t ? '#fff' : '#6B7280',
            }}
          >
            {t === 'all' ? 'All' : TYPE_LABELS[t] || t}
          </button>
        ))}

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: 8, border: '1.5px solid #d1d5db',
            fontSize: 13, color: '#374151', background: '#fff',
          }}
        >
          <option value="">All Statuses</option>
          {Object.keys(STATUS_COLORS).map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Search campaigns..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: 8, border: '1.5px solid #d1d5db',
            fontSize: 13, flex: 1, minWidth: 200,
          }}
        />
      </div>

      {/* Table — wrapped in a white card so it reads against the grey
          page. Pre-2026-05-22 the table sat directly on the page with the
          header bg, row borders, AND page surface all #F3F3F3 → zero
          separation, rows blended together. */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>Loading...</div>
      ) : (
        <div style={{
          background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB',
          overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
              {['Name', 'Type', 'Status', 'Steps', 'Audience', 'Delivered', 'Seen', 'Engagements', 'CTR', 'Created'].map((h) => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c) => (
              <tr
                key={c.id}
                onClick={() => openDetail(c)}
                style={{ borderBottom: '1px solid #EEF0F2', cursor: 'pointer' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#F0FDF4'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}
              >
                <td style={tdStyle}>
                  <div style={{ fontWeight: 600, color: '#111827' }}>{c.name}</div>
                </td>
                <td style={tdStyle}>
                  <span style={{
                    padding: '3px 10px', borderRadius: 100, fontSize: 11, fontWeight: 600,
                    background: TYPE_COLORS[c.campaign_type]?.bg || '#F3F4F6',
                    color: TYPE_COLORS[c.campaign_type]?.text || '#374151',
                  }}>
                    {TYPE_LABELS[c.campaign_type] || c.campaign_type}
                  </span>
                </td>
                <td style={tdStyle}>
                  <span style={{
                    padding: '3px 10px', borderRadius: 100, fontSize: 12, fontWeight: 600,
                    background: STATUS_COLORS[c.status]?.bg || '#F3F4F6',
                    color: STATUS_COLORS[c.status]?.text || '#374151',
                  }}>
                    {c.status}
                  </span>
                </td>
                <td style={tdStyle}>{c.step_count}</td>
                <td style={tdStyle}>{(c as any).unique_users || '—'}</td>
                <td style={tdStyle}>{c.impressions}</td>
                <td style={tdStyle}>{c.seen ?? '—'}</td>
                <td style={tdStyle}>{c.cta_taps || 0}</td>
                <td style={tdStyle}>{c.ctr > 0 ? `${(c.ctr * 100).toFixed(1)}%` : '—'}</td>
                <td style={tdStyle}>{new Date(c.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {campaigns.length === 0 && (
              <tr>
                <td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: '#9CA3AF', padding: 40 }}>
                  No campaigns found
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      )}

      {/* Detail Panel (slide-over overlay) */}
      {selectedCampaign && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.3)', zIndex: 50,
            display: 'flex', justifyContent: 'flex-end',
          }}
          onClick={() => setSelectedCampaign(null)}
        >
          <div
            style={{
              width: 480, background: '#fff', height: '100%',
              boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
              overflowY: 'auto', padding: 32,
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
              <div>
                <span style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: 100, fontSize: 11, fontWeight: 600, marginBottom: 8,
                  background: TYPE_COLORS[selectedCampaign.campaign_type]?.bg || '#F3F4F6',
                  color: TYPE_COLORS[selectedCampaign.campaign_type]?.text || '#374151',
                }}>
                  {TYPE_LABELS[selectedCampaign.campaign_type] || selectedCampaign.campaign_type}
                </span>
                <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#111827' }}>
                  {selectedCampaign.name}
                </h2>
              </div>
              <button
                onClick={() => setSelectedCampaign(null)}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9CA3AF', padding: 4 }}
              >
                &times;
              </button>
            </div>

            {/* Meta row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
              <MetaField label="Status" value={selectedCampaign.status} />
              <MetaField label="Steps" value={String(selectedCampaign.step_count)} />
              <MetaField label="Created" value={new Date(selectedCampaign.created_at).toLocaleDateString()} />
              <MetaField label="Updated" value={new Date(selectedCampaign.updated_at).toLocaleDateString()} />
            </div>

            {/* Analytics Section */}
            <div style={{ marginBottom: 28 }}>
              <label style={sectionLabelStyle}>Analytics</label>
              {panelLoading ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#9CA3AF' }}>Loading analytics...</div>
              ) : analytics ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {/* Engagement funnel: Delivered → Seen → {Tapped CTA | Closed | Ignored}. */}
                  <AnalyticsCard label="Delivered" value={analytics.delivered ?? analytics.impressions} bg="#F3F4F6" color="#374151" />
                  <AnalyticsCard label="Seen" value={analytics.seen ?? 0} bg="#DBEAFE" color="#1E40AF" />
                  <AnalyticsCard label="Tapped CTA" value={analytics.cta_taps} bg="#D1FAE5" color="#065F46" />
                  <AnalyticsCard label="Closed (X)" value={analytics.closed ?? 0} bg="#FEE2E2" color="#991B1B" />
                  <AnalyticsCard label="Ignored" value={analytics.ignored ?? 0} bg="#EDE9FE" color="#5B21B6" />
                  <AnalyticsCard label="CTR (of seen)" value={`${(analytics.ctr * 100).toFixed(1)}%`} bg="#FEF3C7" color="#92400E" />
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 20, color: '#9CA3AF' }}>No analytics available</div>
              )}
            </div>

            {/* Survey Results Section (survey type only) */}
            {selectedCampaign.campaign_type === 'survey' && (
              <div style={{ marginBottom: 28 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <label style={{ ...sectionLabelStyle, marginBottom: 0 }}>Survey Results</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => handleExport('xlsx')}
                      disabled={!!exportingFormat}
                      style={{
                        padding: '5px 12px', borderRadius: 6, border: '1px solid #d1d5db',
                        background: '#fff', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        opacity: exportingFormat ? 0.6 : 1,
                      }}
                    >
                      {exportingFormat === 'xlsx' ? 'Exporting...' : 'Export XLSX'}
                    </button>
                    <button
                      onClick={() => handleExport('csv')}
                      disabled={!!exportingFormat}
                      style={{
                        padding: '5px 12px', borderRadius: 6, border: '1px solid #d1d5db',
                        background: '#fff', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        opacity: exportingFormat ? 0.6 : 1,
                      }}
                    >
                      {exportingFormat === 'csv' ? 'Exporting...' : 'Export CSV'}
                    </button>
                  </div>
                </div>

                {panelLoading ? (
                  <div style={{ textAlign: 'center', padding: 20, color: '#9CA3AF' }}>Loading survey results...</div>
                ) : surveyResults ? (
                  <>
                    {/* Summary */}
                    <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                      <div style={{ flex: 1, padding: 12, background: '#F9FAFB', borderRadius: 8, textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>{surveyResults.total_responses}</div>
                        <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 500 }}>Total Responses</div>
                      </div>
                      <div style={{ flex: 1, padding: 12, background: '#F9FAFB', borderRadius: 8, textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>
                          {(surveyResults.completion_rate * 100).toFixed(1)}%
                        </div>
                        <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 500 }}>Completion Rate</div>
                      </div>
                    </div>

                    {/* Completion Funnel */}
                    {surveyResults.funnel && surveyResults.funnel.length > 0 && (
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Completion Funnel
                        </div>
                        {surveyResults.funnel.map((step, i) => {
                          const maxAnswered = surveyResults.funnel[0]?.answered || 1;
                          const pct = maxAnswered > 0 ? (step.answered / maxAnswered) * 100 : 0;
                          return (
                            <div key={step.question_id} style={{ marginBottom: 6 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6B7280', marginBottom: 2 }}>
                                <span>Q{i + 1}</span>
                                <span>{step.answered}</span>
                              </div>
                              <div style={{ height: 8, background: '#F3F4F6', borderRadius: 4, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${pct}%`, background: '#1F6C4F', borderRadius: 4, transition: 'width 0.3s' }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Per-Question Breakdown */}
                    {surveyResults.questions.map((q, qi) => (
                      <div key={q.question_id} style={{ marginBottom: 20, padding: 14, background: '#F9FAFB', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 4 }}>
                          Q{qi + 1}. {q.question_text}
                        </div>
                        <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 10 }}>
                          {q.total_responses} responses &middot; {q.response_type.replace('_', '/')}
                        </div>

                        {/* Yes/No bars */}
                        {q.response_type === 'yes_no' && (
                          <div>
                            {['yes', 'no'].map(key => {
                              const count = q.responses[key] || 0;
                              const pct = q.total_responses > 0 ? (count / q.total_responses) * 100 : 0;
                              return (
                                <div key={key} style={{ marginBottom: 4 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                                    <span style={{ color: '#374151', fontWeight: 500, textTransform: 'capitalize' }}>{key}</span>
                                    <span style={{ color: '#6B7280' }}>{count} ({pct.toFixed(0)}%)</span>
                                  </div>
                                  <div style={{ height: 12, background: '#E5E7EB', borderRadius: 4, overflow: 'hidden' }}>
                                    <div style={{
                                      height: '100%', borderRadius: 4, transition: 'width 0.3s',
                                      width: `${pct}%`,
                                      background: key === 'yes' ? '#34D399' : '#9CA3AF',
                                    }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Rating bars (1-5) */}
                        {q.response_type === 'rating' && (
                          <div>
                            {[1, 2, 3, 4, 5].map(rating => {
                              const count = q.responses[String(rating)] || 0;
                              const pct = q.total_responses > 0 ? (count / q.total_responses) * 100 : 0;
                              return (
                                <div key={rating} style={{ marginBottom: 4 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                                    <span style={{ color: '#374151', fontWeight: 500 }}>{rating} star{rating !== 1 ? 's' : ''}</span>
                                    <span style={{ color: '#6B7280' }}>{count} ({pct.toFixed(0)}%)</span>
                                  </div>
                                  <div style={{ height: 12, background: '#E5E7EB', borderRadius: 4, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${pct}%`, background: '#F59E0B', borderRadius: 4, transition: 'width 0.3s' }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Multiple choice bars */}
                        {q.response_type === 'multiple_choice' && (
                          <div>
                            {Object.entries(q.responses).map(([option, count]) => {
                              const pct = q.total_responses > 0 ? (count / q.total_responses) * 100 : 0;
                              return (
                                <div key={option} style={{ marginBottom: 4 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                                    <span style={{ color: '#374151', fontWeight: 500 }}>{option}</span>
                                    <span style={{ color: '#6B7280' }}>{count} ({pct.toFixed(0)}%)</span>
                                  </div>
                                  <div style={{ height: 12, background: '#E5E7EB', borderRadius: 4, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${pct}%`, background: '#6366F1', borderRadius: 4, transition: 'width 0.3s' }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Free text responses */}
                        {q.response_type === 'free_text' && q.free_text_responses && (
                          <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff' }}>
                            {q.free_text_responses.map((text, i) => (
                              <div
                                key={i}
                                style={{
                                  padding: '8px 12px', fontSize: 13, color: '#374151', lineHeight: 1.5,
                                  borderBottom: i < q.free_text_responses!.length - 1 ? '1px solid #f3f4f6' : 'none',
                                }}
                              >
                                {text}
                              </div>
                            ))}
                            {q.free_text_responses.length === 0 && (
                              <div style={{ padding: '12px', fontSize: 13, color: '#9CA3AF', textAlign: 'center' }}>No responses</div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: 20, color: '#9CA3AF' }}>No survey results available</div>
                )}
              </div>
            )}

            {/* Edit Campaign link */}
            <button
              onClick={() => navigate(`/admin/campaigns/${selectedCampaign.id}`)}
              style={{
                width: '100%', padding: '10px 20px', borderRadius: 8,
                background: '#1F6C4F', color: '#fff', border: 'none',
                fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 16,
              }}
            >
              Edit Campaign
            </button>

            {/* Reference */}
            <div style={{ fontSize: 12, color: '#9CA3AF' }}>
              ID: {selectedCampaign.id}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Helper Components ─────────────────────────────────────────────────────────

const MetaField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
      {label}
    </div>
    <div style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>{value}</div>
  </div>
);

const AnalyticsCard: React.FC<{ label: string; value: number | string; bg: string; color: string }> = ({ label, value, bg, color }) => (
  <div style={{ padding: 14, borderRadius: 14, background: bg, boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
    <div style={{ fontSize: 11, fontWeight: 600, color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 700, color, marginTop: 2 }}>{value}</div>
  </div>
);

const sectionLabelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, color: '#6B7280',
  marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em',
};

const thStyle: React.CSSProperties = {
  // color darkened #9CA3AF → #6B7280 (2026-05-22) for legible header
  // labels against the light header surface.
  padding: '10px 16px', textAlign: 'left', fontSize: 12,
  fontWeight: 600, color: '#6B7280', textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px', verticalAlign: 'top',
};
